<template>
    <v-row no-gutters class="mb-3">
        <v-col cols="12">
            <div
                style="background: #fff; overflow: hidden"
                class="rounded-lg d-flex align-center justify-space-between mt-2 pa-1"
            >
                <div style="width: 20%">
                    <v-select-filled
                        v-model="activeTab"
                        :items="types"
                        label="Type"
                        hide-details
                        density="compact"
                        item-title="text"
                    >
                        <template #item="{ props, item }">
                            <v-divider
                                v-if="
                                    item.raw.divider || (item.raw.props && item.raw.props.divider)
                                "
                            />
                            <v-list-subheader
                                v-else-if="
                                    item.raw.header || (item.raw.props && item.raw.props.header)
                                "
                                >{{ item.raw.header }}</v-list-subheader
                            >
                            <v-list-item
                                v-else
                                v-bind="props"
                                :value="item.value"
                                class="text-capitalize"
                            >
                                <!-- {{ item.title }} -->
                            </v-list-item>
                        </template>
                    </v-select-filled>
                </div>
                <div style="width: 30%">
                    <v-text-field-filled
                        v-model="search"
                        append-inner-icon="$search"
                        clearable
                        @click:clear="search = ''"
                        label="Rechercher"
                        hide-details
                    />
                </div>
            </div>
        </v-col>
        <v-col cols="12">
            <v-row no-gutters v-if="loadingConnectors || loadingConnectedBankAccounts">
                <v-col cols="12" class="d-flex align-center justify-center py-3">
                    <v-progress-circular indeterminate size="64" color="primary" />
                </v-col>
            </v-row>
            <v-row no-gutters v-else-if="!hasFilteredConnections">
                <v-col cols="12" class="d-flex align-center justify-center py-3">
                    Aucun connecteur trouvé
                </v-col>
            </v-row>
            <ConnectorsTable
                :connectedBankAccounts="filteredConnectedBankAccounts"
                :connectors="filteredConnectors"
                :companyId="companyId"
                :displayAddConnectorCard="true"
                @updateProfile="(authValues) => upsertSetting(authValues)"
            />
        </v-col>
    </v-row>
</template>

<script lang="ts">
import _ from "lodash";
import BANK_ACCOUNTS from "@/graphql/bank/BankAccountsWithBTCount.gql";
import GET_CONNECTORS from "@/graphql/connector/GetCompanyConnectors.gql";
// import GET_CONNECTORS from "@/graphql/connector/GetConnectors.gql"
import UPSERT_CONNECTOR_SETTING from "@/graphql/UpsertConnectorSetting.gql";
import { Apollo } from "@/decorators";
import SettingCard from "../SettingCard.vue";
import SettingConnector from "../settings/SettingConnector.vue";

import { Offer_type } from "@/types/offer.types";
import { ConnectedBankAccount } from "@/types/bankAccounts.types";
import { ConnectorServices } from "@/services/connectorServices";

import ConnectorsTable from "./ConnectorsTable.vue";
import { Component, Vue, Watch, toNative } from "vue-facing-decorator";
import { Prop } from "vue-facing-decorator";
import { OfferAccessRight } from "@/services/accessRightServices";

@Component({
    components: {
        SettingCard,
        SettingConnector,
        ConnectorsTable,
    },
})
export class Connectors extends Vue {
    @Prop({ default: "" }) companyId: string;

    activeTab: string = "ALL";

    search: string = "";

    upsertConfig: any = "";

    loadingConnectors: boolean = false;

    loadingConnectedBankAccounts: boolean = false;

    filteredConnectors: any[] = [];

    filteredConnectedBankAccounts: ConnectedBankAccount[] = [];

    @Apollo({
        query: BANK_ACCOUNTS,
        variables() {
            this.loadingConnectedBankAccounts = true;
            return {
                companyId: this.companyId,
                type: "BANK",
            };
        },
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading, networkStatus }: any) {
            if (!loading) {
                if (data?.bankAccounts?.length > 0) {
                    this.connectedBankAccounts = data.bankAccounts.filter(
                        (account: ConnectedBankAccount) => account.budgetInsightId != null,
                    );
                } else {
                    this.connectedBankAccounts = [];
                }
                this.loadingConnectedBankAccounts = false;
            }
        },
    })
    connectedBankAccounts: readonly ConnectedBankAccount[] = [];

    @Apollo({
        query: GET_CONNECTORS,
        variables() {
            this.loadingConnectors = true;
            return {
                companyId: this.companyId,
            };
        },
        skip() {
            return !this.companyId;
        },
        specificResult({ data, loading }: any) {
            if (!loading) {
                if (data) {
                    this.loadingConnectors = false;
                    // this.connections = data.getConnectors.connectors
                    this.connectors = data.getCompanyConnectors.connectors;
                    debugger;
                } else {
                    this.connectors = [];
                }
            }
        },
    })
    connectors: any[] = [];

    /**
     * Apply filters to the connected bank accounts and connectors
     */
    applyFilters(): void {
        switch (this.activeTab) {
            case "ALL":
                this.filterConnectionsActiveTabAll();
                console.log("hasFilteredConnections", this.hasFilteredConnections);
                break;
            case "MY_CONNECTORS":
                this.filteredConnectors = this.connectors.filter(
                    (connections) => connections.active,
                );
                this.filteredConnectedBankAccounts = [];
                break;
            case "BANK":
                this.filteredConnectors = [];
                this.filteredConnectedBankAccounts = _.cloneDeep(this.connectedBankAccounts) as any;
                break;
            default:
                this.filteredConnectors = this.connectors.filter(
                    (connection) => connection.type === this.activeTab,
                );
                this.filteredConnectedBankAccounts = [];
                break;
        }

        this.filterConnectionsBySearchInput();
    }

    /**
     * Apply filters when a tab is selected or search is made
     */
    @Watch("activeTab", { immediate: true })
    @Watch("search", { immediate: true })
    onFilterOptionChanged() {
        this.applyFilters();
    }

    /**
     * Apply filters to the connected bank accounts and connectors
     * when the data changes
     */
    @Watch("connectors")
    @Watch("connectedBankAccounts")
    onInitialDataChanged() {
        this.applyFilters();
    }

    /**
     * Return true if there are connections
     */
    get hasFilteredConnections(): boolean {
        return this.filteredConnectors.length > 0 || this.filteredConnectedBankAccounts.length > 0;
    }

    get collectType() {
        return ["INVOICE", "E-COMMERCE", "POINT_OF_SALE"];
    }

    get uniqueTypes() {
        return [...new Set(this.connectors.map((objet) => objet.type))];
    }

    get offerType() {
        return this.$store.state.offerType;
    }

    get isAllowedTo() {
        // switch (this.offerType) {
        // case Offer_type.ACCOUNTING_ESSENTIAL:
        // case Offer_type.ACCOUNTING_PREMIUM:
        // case Offer_type.BUSINESS_ESSENTIAL:
        // case Offer_type.BUSINESS_PREMIUM:
        // case Offer_type.TRAINING:
        // return true;
        // default:
        // return false;
        // }
        return OfferAccessRight.checkOfferAccessRight("isAllowedToConnector", false);
    }

    get types() {
        const typeTab = [
            {
                text: "Tous",
                value: "ALL",
            },
            {
                divider: true,
            },
            {
                header: "Connecteur",
            },
            {
                text: "Banque",
                value: "BANK",
            },
            {
                text: "Facture",
                value: "INVOICE",
            },
            {
                text: "Facturation",
                value: "INVOICING",
            },
            {
                text: "E-commerce",
                value: "E-COMMERCE",
            },
            {
                text: "Point de vente",
                value: "POINT_OF_SALE",
            },
        ];

        if (this.isAllowedTo) {
            typeTab.push({ divider: true });
            typeTab.push({
                text: "Service",
                value: "COLLECT",
            });
        }

        return typeTab;
    }

    getLogo(application: string) {
        return ConnectorServices.getLogo(application);
    }

    getIntegrationStatus(status) {
        if (status === "active") {
            return true;
        } else {
            return false;
        }
    }

    async upsertSetting(configValue) {
        this.upsertConfig = await ConnectorServices.upsertSettingHelper(
            configValue,
            this.companyId,
        );
    }

    /**
     * Filter connected bank accounts and connectors for the active tab "ALL"
     */
    private filterConnectionsActiveTabAll(): void {
        this.filteredConnectedBankAccounts = _.cloneDeep(this.connectedBankAccounts) as any;

        if (this.isAllowedTo) {
            this.filteredConnectors = this.connectors;
        } else {
            const filteredConnectors = [];
            for (const connection of this.connectors) {
                for (const type of this.collectType) {
                    if (connection.type === type) {
                        filteredConnectors.push(connection);
                        break;
                    }
                }
            }

            this.filteredConnectors = filteredConnectors;
        }
    }

    /**
     * Filter connected bank accounts and connectors by using the value of the search input
     */
    private filterConnectionsBySearchInput(): void {
        if (this.search?.length > 0) {
            this.filteredConnectors = this.filteredConnectors.filter((connection) =>
                connection.name?.toLowerCase().includes(this.search.toLowerCase().trim()),
            );

            this.filteredConnectedBankAccounts = _.cloneDeep(
                this.filteredConnectedBankAccounts,
            ).filter(
                (account: ConnectedBankAccount) =>
                    account.name?.toLowerCase().includes(this.search?.toLowerCase().trim()) ||
                    account.bank?.name?.toLowerCase().includes(this.search.toLowerCase().trim()),
            );
        }
    }
}

export default toNative(Connectors);
</script>
